import { IDatasetTypeMetadata, IDynamicDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { googleBigQueryObjectMetadata } from './google-big-query-object-metadata';
import { format } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { GoogleBigQueryV2Versions } from '../linked-service/google-bigquery-v2-metadata';
import { preCopyScriptMetadata } from '../consts/metadata-consts';
import { Constants } from 'shell-models-lib';
import { ColumnPhysicalType, ColumnType } from 'app/shared/utils/column-type.util';

const physicalToPhysicalMapping: { [key: string]: string } = {
    record: ColumnPhysicalType.String,
    array: ColumnPhysicalType.String
};

export const googleBigQueryObjectV2Metadata: IDynamicDatasetTypeMetadata = async () => {
    const metadata: IDatasetTypeMetadata = {
        ...googleBigQueryObjectMetadata,
        warningMessage: undefined,
        warningMessageHyperlink: undefined,
        datasetNamePrefix: adfModelTypeMap.googleBigQuery.datasetType,
        schemaNameDisplay: stringResources.Dataset_nameLabel,
        disabled: false,
        type: __IS_FABRIC_DPE__ ? adfModelTypeMap.googleBigQuery.datasetType : adfModelTypeMap.googleBigQueryV2.datasetType,
        isSink: __IS_FABRIC_DPE__,
        displayName: stringResources.GoogleBigQuery_DisplayText,
        queryDisplayName: stringResources.GoogleBigQuery_QueryLabel,
        queryTooltipLink: 'https://cloud.google.com/bigquery/docs/reference/standard-sql/query-syntax',
        supportLinkedServiceTypes: [__IS_FABRIC_DPE__ ? adfModelTypeMap.googleBigQuery.linkedServiceType : adfModelTypeMap.googleBigQueryV2.linkedServiceType],
        versionConfig: GlobalVariables.enableGBQVersion && __IS_FABRIC_DPE__ ? [
            {
                label: GoogleBigQueryV2Versions.V1_1,
                value: GoogleBigQueryV2Versions.V1_1,
                description: format(stringResources.Connector_Version_Description, 'https://learn.microsoft.com/fabric/data-factory/connector-google-bigquery-copy-activity'),
                escapeDescription: false
            },
            {
                label: GoogleBigQueryV2Versions.V1_0,
                value: GoogleBigQueryV2Versions.V1_0
            }
        ] : undefined,
        defaultVersion: GlobalVariables.enableGBQVersion && __IS_FABRIC_DPE__ ? '1.0' : undefined,
        copySinkControls: __IS_FABRIC_DPE__ ? [
            preCopyScriptMetadata(stringResources.GBQ_PreCopyScript_Description),
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'writeBatchTimeout',
                    label: stringResources.ModelName_WriteBatchTimeout,
                    validators: [
                        {
                            type: 'pattern',
                            pattern: Constants.timeoutValidationPattern,
                            errorMessage: format(stringResources.InvalidTimeoutPattern, stringResources.ModelName_WriteBatchTimeout)
                        }
                    ]
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox-number',
                    bind: 'writeBatchSize',
                    label: stringResources.ModelName_WriteBatchSize,
                    inputType: 'number',
                    min: 0
                },
                dynamicContentTextClass: 'input-spaced'
            }
        ] : [],
        copyJob: {
            multipleQuery: true,
            sourcePartition: {
                supportSourcePartition: GlobalVariables.copyJobBigQuerySourcePartition
            }
        },
        notSupportPhysicalType: __IS_FABRIC_DPE__ ? !GlobalVariables.gbqAutoCreate : true,
        supportSameTypePhysicalCopy: GlobalVariables.gbqAutoCreate,
        mappingKeyToLowerCase: GlobalVariables.gbqAutoCreate,
        supportAutoTableCreation: GlobalVariables.gbqAutoCreate,
        supportEditPhysicalType: GlobalVariables.gbqAutoCreate,
        supportedPhysicalTypes: GlobalVariables.gbqAutoCreate ? [
            ColumnPhysicalType.Boolean,
            ColumnPhysicalType.Bytes,
            ColumnPhysicalType.Date,
            ColumnPhysicalType.Time,
            ColumnPhysicalType.Datetime,
            ColumnPhysicalType.Timestamp,
            ColumnPhysicalType.Integer,
            ColumnPhysicalType.Float,
            ColumnPhysicalType.Numeric,
            ColumnPhysicalType.Bignumeric,
            ColumnPhysicalType.String,
            ColumnPhysicalType.JSON,
            ColumnPhysicalType.Geography
        ] : undefined,
        LogicalToPhysicalMapping: GlobalVariables.gbqAutoCreate ? {
            boolean: ColumnPhysicalType.Boolean,
            'byte[]': ColumnPhysicalType.Bytes,
            date: ColumnPhysicalType.Date,
            time: ColumnPhysicalType.Time,
            datetime: ColumnPhysicalType.Datetime,
            datetimeoffset: ColumnPhysicalType.Timestamp,
            timespan: ColumnPhysicalType.Integer,
            sbyte: ColumnPhysicalType.Integer,
            byte: ColumnPhysicalType.Integer,
            int16: ColumnPhysicalType.Integer,
            uint16: ColumnPhysicalType.Integer,
            int32: ColumnPhysicalType.Integer,
            uint32: ColumnPhysicalType.Integer,
            int64: ColumnPhysicalType.Integer,
            uint64: ColumnPhysicalType.Integer,
            single: ColumnPhysicalType.Float,
            double: ColumnPhysicalType.Float,
            decimal: ColumnPhysicalType.Numeric,
            string: ColumnPhysicalType.String,
            guid: ColumnPhysicalType.String
        } : undefined,
        PhysicalToLogicalMapping: GlobalVariables.gbqAutoCreate ? {
            boolean: ColumnType.Boolean,
            bytes: ColumnType.ByteArray,
            date: ColumnType.Date,
            time: ColumnType.Time,
            datetime: ColumnType.DateTime,
            timestamp: ColumnType.String,
            integer: ColumnType.Int64,
            float: ColumnType.Double,
            numeric: ColumnType.Decimal,
            bignumeric: ColumnType.Decimal,
            string: ColumnType.String
        } : undefined,
        physicalToPhysicalMapping: GlobalVariables.gbqAutoCreate ? {
            [adfModelTypeMap.googleBigQueryV2.datasetType]: physicalToPhysicalMapping,
            [adfModelTypeMap.googleBigQuery.datasetType]: physicalToPhysicalMapping,
        } : undefined,
        supportedSinkPhysicalTypesFromLogicalSource: GlobalVariables.gbqAutoCreate ? {
            boolean: [ColumnPhysicalType.Boolean, ColumnPhysicalType.String],
            'byte[]': [ColumnPhysicalType.Bytes, ColumnPhysicalType.String],
            date: [ColumnPhysicalType.Date, ColumnPhysicalType.String, ColumnPhysicalType.Datetime, ColumnPhysicalType.Timestamp],
            time: [ColumnPhysicalType.Time, ColumnPhysicalType.String],
            datetime: [ColumnPhysicalType.Datetime, ColumnPhysicalType.String, ColumnPhysicalType.Timestamp],
            datetimeoffset: [ColumnPhysicalType.Timestamp, ColumnPhysicalType.String],
            timespan: [ColumnPhysicalType.Integer, ColumnPhysicalType.String, ColumnPhysicalType.Float, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            sbyte: [ColumnPhysicalType.Integer, ColumnPhysicalType.String, ColumnPhysicalType.Float, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            byte: [ColumnPhysicalType.Integer, ColumnPhysicalType.String, ColumnPhysicalType.Float, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            int16: [ColumnPhysicalType.Integer, ColumnPhysicalType.String, ColumnPhysicalType.Float, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            uint16: [ColumnPhysicalType.Integer, ColumnPhysicalType.String, ColumnPhysicalType.Float, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            int32: [ColumnPhysicalType.Integer, ColumnPhysicalType.String, ColumnPhysicalType.Float, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric] ,
            uint32: [ColumnPhysicalType.Integer, ColumnPhysicalType.String, ColumnPhysicalType.Float, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            int64: [ColumnPhysicalType.Integer, ColumnPhysicalType.String, ColumnPhysicalType.Float, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            uint64: [ColumnPhysicalType.Integer, ColumnPhysicalType.String, ColumnPhysicalType.Float, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            single: [ColumnPhysicalType.Float, ColumnPhysicalType.String, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            double: [ColumnPhysicalType.Float, ColumnPhysicalType.String, ColumnPhysicalType.Numeric, ColumnPhysicalType.Bignumeric],
            decimal: [ColumnPhysicalType.Numeric, ColumnPhysicalType.String, ColumnPhysicalType.Bignumeric]
        } : undefined,
        binaryPhysicalTypes: GlobalVariables.gbqAutoCreate ? [
            ColumnPhysicalType.String,
            ColumnPhysicalType.Bytes
        ] : undefined,
        binaryLengthMap: GlobalVariables.gbqAutoCreate ? {
            // in GBQ, negative, 0, & null all mean max length
            string: {
                minValue: null, 
                maxValue: null,
                defaultValue: null,
                supportMaxValue: false
            },
            bytes: {
                minValue: null,
                maxValue: null,
                defaultValue: null,
                supportMaxValue: false
            }
        } : undefined,
        precisionMap: GlobalVariables.gbqAutoCreate ? {
            numeric: {
                minValue: 1,
                maxValue: 38,
                defaultValue: 38
            },
            bignumeric: {
                minValue: 1,
                maxValue: 76,
                defaultValue: 76
            }
        } : undefined,
        scaleMap: GlobalVariables.gbqAutoCreate ? {
            numeric: {
                minValue: 0,
                maxValue: 9,
                defaultValue: 9
            },
            bignumeric: {
                minValue: 0,
                maxValue: 38,
                defaultValue: 38
            }
        } : undefined
    };
    return metadata;
};
